// Backend API service for secure chat operations
// This replaces direct OpenAI/Pinecone calls with backend API calls

export interface BackendChatResponse {
  answer: string;
  citations: Array<{
    id: string;
    text: string;
    title: string;
    relevanceScore: number;
  }>;
  usage: {
    tokens: number;
  };
}

export interface BackendSummaryResponse {
  summary: string;
  workInfo: {
    title: string;
    philosopher: string;
    timestamp: string;
  };
}

export class BackendChatService {
  private apiBaseUrl: string;

  constructor() {
    this.apiBaseUrl = import.meta.env.VITE_CHAT_API_URL || 'https://ouibeef-chat-api.workers.dev';
    console.log(`BackendChatService using API: ${this.apiBaseUrl}`);
  }

  async chat(
    question: string, 
    philosopher: 'adam-smith' | 'karl-marx',
    temperature: number = 0.7
  ): Promise<BackendChatResponse> {
    const response = await fetch(`${this.apiBaseUrl}/api/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        question,
        philosopher,
        temperature,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error');
      throw new Error(`Chat API error (${response.status}): ${errorText}`);
    }

    return response.json();
  }

  async getSummary(
    workTitle: string,
    philosopher: 'adam-smith' | 'karl-marx'
  ): Promise<BackendSummaryResponse> {
    const response = await fetch(`${this.apiBaseUrl}/api/summary`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        workTitle,
        philosopher,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error');
      throw new Error(`Summary API error (${response.status}): ${errorText}`);
    }

    return response.json();
  }
}

export const backendChatService = new BackendChatService();